import React, { useState, useEffect, useCallback, useMemo, ChangeEvent, useRef } from 'react';
import { Code, Eye, Link, PlusCircle, Save, Trash2, X } from 'lucide-react';
import ReactQuill from 'react-quill';
import GlobalDrawer from '../../components/GlobalDrawer';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import  PopupComponent  from '../../components/PopupComponent';
import EmailPreview from './PreviewModal';
import "react-quill/dist/quill.snow.css";
import GenericConfirmationModal from '../../components/GenericConfirmationModal';
import { createNewContent, deleteContent, updateContent } from '../../services/contentService';
import logger, { Logger } from '../../utils/logger';

interface EmailData {

  title: string;
  tags: string[];
  subject: string;
  message: string;
  type?: string;
  files?: File[];
  personalizedFields?: Array<{value: string, label: string}>;
  isHtmlTemplate?: boolean;
}

interface PersonalizationField {
  value: string;
  label: string;
  type?: string;
}


interface Tag {
  name: string;
  value: string;
}

interface EmailDashboardProps {
  openEmailBox: boolean;
  setOpenEmailBox: (value: boolean) => void;
  activeId: string;
  handleFormSubmit: (data: EmailData) => Promise<void>;
  messageTag: Tag[];
  initialData: EmailData;
  onSuccessfulSubmit?: () => Promise<void>; 
  
  availableFields?: PersonalizationField[];
}

// Styled Components
const StyledEmailEditor = styled.div`
  font-family: "Inter", sans-serif;
  background-color: #f8f9fa;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 20px;
  width: 100%;
  min-width: 800px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-right: 48px;
`;

const Button = styled.button<{ primary?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 8px;

  ${(props) =>
    props.primary
      ? `
    background-color: #3498db;
    color: white;
    &:hover {
      background-color: #2980b9;
    }
    svg {
      color: white;
    }
  `
      : `
    background-color: #e9ecef;
    color: #495057;
    &:hover {
      background-color: #dee2e6;
    }
  `}

  svg {
    margin-right: 6px;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }

  &:hover {
    border-color: #adb5bd;
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
  padding: 4px;
`;

const StyledTag = styled.span<{ active: boolean }>`
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) => (props.active ? "#3498db" : "#f0f2f5")};
  color: ${(props) => (props.active ? "white" : "#4b5563")};
  text-transform: capitalize;
  margin: 4px;
  font-weight: 500;
  letter-spacing: 0.3px;
  box-shadow: ${(props) =>
    props.active ? "0 2px 4px rgba(52,152,219,0.2)" : "none"};

  &:hover {
    background-color: ${(props) => (props.active ? "#2980b9" : "#e5e7eb")};
    transform: translateY(-1px);
  }
`;

const StyledReactQuill = styled(ReactQuill)`
  .ql-container {
    font-size: 14px;
    background: #fff;
    border-color: #ced4da;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    min-height: 200px;
  }
  
  .ql-editor {
    min-height: 200px;
    font-size: 14px;
    line-height: 1.5;
  }

  .ql-toolbar {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background: #fff;
    border-color: #ced4da;
  }
`;

const EmailDashboard: React.FC<EmailDashboardProps> = ({
  openEmailBox,
  setOpenEmailBox,
  activeId,
  handleFormSubmit,
  messageTag,
  initialData,
  onSuccessfulSubmit,
  availableFields = [],
}) => {
  Logger.log('EmailDashboard rendered:', { openEmailBox, activeId, initialData });

  // State management
  const [emailData, setEmailData] = useState<EmailData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isFieldDrawerOpen, setIsFieldDrawerOpen] = useState(false);
  const editorRef = useRef<ReactQuill | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

const [rawHtmlContent, setRawHtmlContent] = useState<string>('');

const [showHtmlImportModal, setShowHtmlImportModal] = useState(false);
const [htmlImportContent, setHtmlImportContent] = useState('');
const [htmlImportError, setHtmlImportError] = useState('');
const [isHtmlTemplate, setIsHtmlTemplate] = useState(false);

  const [previewData, setPreviewData] = useState<{
    isOpen: boolean;
    subject: string;
    content: string;
    attachments?: File[];
  }>({
    isOpen: false,
    subject: '',
    content: '',
    attachments: undefined
  });

  // Reset form when initialData changes

useEffect(() => {
  Logger.log('EmailDashboard useEffect - initialData changed:', initialData);
  if (openEmailBox) {
    setEmailData(initialData);
    
    // Check if this is an HTML template
    if (initialData.isHtmlTemplate) {
      Logger.log('Loading HTML template content');
      setIsHtmlTemplate(true);
      setRawHtmlContent(initialData.message);
    } else {
      setIsHtmlTemplate(false);
      setRawHtmlContent('');
    }
    
    setHasUnsavedChanges(false);
  }
}, [initialData, openEmailBox]);



  const reduxFields = useSelector((state: any) => 
    state?.rootReducers?.userData?.userDetails?.userPreference?.customForm?.map(
      (item: any) => ({
        value: item?.value,
        label: item?.name,
        type: item?.type,
      })
    ) || []
  );

  const handleDelete = useCallback(async () => {
    if (!activeId) return;
    
    try {
      const response = await deleteContent(activeId);
      
      if (response.status === 200) {
        toast.success('Email template deleted successfully');
        setOpenEmailBox(false);
        
        // Call the refresh function if it exists
        if (onSuccessfulSubmit) {
          await onSuccessfulSubmit();
        }
      } else {
        toast.error('Failed to delete email template');
      }
    } catch (error) {
      console.error('Error deleting email template:', error);
      toast.error('Error deleting email template');
    }
  }, [activeId, setOpenEmailBox, onSuccessfulSubmit]);
  

  
  const handleFieldInsertion = useCallback((field: PersonalizationField) => {
    Logger.log('Field Insertion Called:', { field, editorRef: editorRef.current });

    if (editorRef.current) {
      Logger.log('Editor Ref Exists');
      try {
        // Use optional chaining and nullish coalescing
        const quill = editorRef.current.getEditor?.() ?? null;
        Logger.log('Quill Editor:', quill);

        if (quill) {
          Logger.log('Quill Methods Available');
          
          const range = quill.getSelection();
          Logger.log('Current Selection Range:', range);

          if (range) {
            quill.insertText(range.index, `{{${field.value}}}`, 'user');
            Logger.log('Text Inserted Successfully');
          } else {
            Logger.warn('No selection range found, inserting at end');
            const textLength = quill.getText().length;
            quill.insertText(textLength, `{{${field.value}}}`, 'user');
          }
        } else {
          console.error('Quill methods not available');
        }
      } catch (error) {
        console.error('Error in field insertion:', error);
      }
    } else {
      console.error('Editor Ref is Null');
    }

    // Always try to close the drawer and mark changes
    setIsFieldDrawerOpen(false);
    setHasUnsavedChanges(true);
  }, []);

  // Form validation
  const validateForm = useCallback(() => {
    const errors: string[] = [];
    if (!emailData.title.trim()) errors.push("Title is required");
    if (!emailData.subject.trim()) errors.push("Subject is required");
    if (!emailData.message.trim()) errors.push("Email content is required");
    return errors;
  }, [emailData]);

  // Handle input changes
  const handleInputChange = useCallback((key: keyof EmailData, value: string | string[]) => {
    Logger.log('handleInputChange:', { key, value });
    setEmailData(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  }, []);

  // Handle tag selection
  const handleTagToggle = useCallback((tagValue: string) => {
    Logger.log('handleTagToggle:', tagValue);
    setEmailData(prev => ({
      ...prev,
      tags: [tagValue],
    }));
    setHasUnsavedChanges(true);
  }, []);


  const handleConfirmClose = useCallback(() => {
    setOpenEmailBox(false);
  }, [setOpenEmailBox]);

  // Handle preview toggle
const handlePreviewToggle = useCallback(() => {
  Logger.log('[handlePreviewToggle] Toggling preview, isHtmlTemplate:', isHtmlTemplate);
  
  // Use the appropriate content based on mode
  const emailContent = isHtmlTemplate ? rawHtmlContent : emailData.message;
  
  Logger.log('[handlePreviewToggle] Preview content type:', isHtmlTemplate ? 'HTML Template' : 'Rich Text');
  
  setPreviewData({
    isOpen: !previewData.isOpen,
    subject: emailData.subject,
    content: emailContent,
    attachments: emailData.files
  });
}, [emailData, previewData.isOpen, isHtmlTemplate, rawHtmlContent]);

const insertUnsubscribeLink = useCallback(() => {
  logger.log('DIRECT LOG: insertUnsubscribeLink button clicked'); // Add this
  Logger.log('[insertUnsubscribeLink] Called, isHtmlTemplate:', isHtmlTemplate);
  // If in HTML template mode, insert directly into the raw HTML
  if (isHtmlTemplate) {
    Logger.log('[insertUnsubscribeLink] Inserting into raw HTML template');
    
    // Create unsubscribe HTML - match style with the template
    const unsubscribeHTML = `
      <p style="text-align: center; font-size: 12px; color: #666;">
        If you no longer wish to receive these emails, you can 
        <a href="{{unsubscribe_link}}" style="color: #666; text-decoration: underline;">unsubscribe here</a>.
      </p>
    `;
    
    try {
      // Try to insert before closing body tag
      if (rawHtmlContent.includes('</body>')) {
        const newContent = rawHtmlContent.replace('</body>', `${unsubscribeHTML}</body>`);
        setRawHtmlContent(newContent);
        setHasUnsavedChanges(true);
        toast.success('Unsubscribe link inserted');
        return;
      }
      
      // If no body tag, try to append at the end before closing html
      if (rawHtmlContent.includes('</html>')) {
        const newContent = rawHtmlContent.replace('</html>', `${unsubscribeHTML}</html>`);
        setRawHtmlContent(newContent);
        setHasUnsavedChanges(true);
        toast.success('Unsubscribe link inserted');
        return;
      }
      
      // If neither tag exists, just append to the end
      const newContent = rawHtmlContent + unsubscribeHTML;
      setRawHtmlContent(newContent);
      setHasUnsavedChanges(true);
      toast.success('Unsubscribe link inserted');
    } catch (error) {
      console.error('Error inserting unsubscribe link:', error);
      toast.error('Failed to insert unsubscribe link');
    }
  } 
  // Otherwise, use the normal ReactQuill editor method
  else if (editorRef.current) {
    try {
      const quill = editorRef.current.getEditor();
      
      if (quill) {
        const range = quill.getSelection() || { index: quill.getText().length, length: 0 };
        
        // HTML for clickable unsubscribe link with explanatory text
        const unsubscribeHTML = `
          <p>If you no longer wish to receive these emails, you can <a href="{{unsubscribe_link}}" style="color: #3498db; text-decoration: underline;">unsubscribe by clicking here</a>.</p>
        `;
        
        // Insert the HTML at cursor position or at the end
        quill.clipboard.dangerouslyPasteHTML(range.index, unsubscribeHTML);
        
        Logger.log('Unsubscribe link inserted successfully in rich text editor');
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.error('Error inserting unsubscribe link:', error);
      toast.error('Failed to insert unsubscribe link');
    }
  }
}, [isHtmlTemplate, rawHtmlContent, editorRef]);

  // Handle save operation
const handleSave = useCallback(async () => {
  Logger.log('[handleSave] Called with data:', emailData);
  Logger.log('[handleSave] isHtmlTemplate:', isHtmlTemplate);
  
  const errors = validateForm();
  if (errors.length > 0) {
    errors.forEach(error => toast.error(error));
    return;
  }

  setIsSubmitting(true);
  try {
    // Prepare the payload in the format expected by the API
    const payload = {
      details: {
        title: emailData.title,
        tags: emailData.tags,
        subject: emailData.subject,
        // Use raw HTML content if in HTML template mode
        message: isHtmlTemplate ? rawHtmlContent : emailData.message,
        // Store a flag to identify HTML templates
        isHtmlTemplate: isHtmlTemplate,
        // Ensure tag is properly set as expected by API
        tag: Array.isArray(emailData.tags) && emailData.tags.length > 0 
          ? emailData.tags[0] 
          : 'no_tag'
      },
      type: 'email_template'
    };

    Logger.log('[handleSave] Prepared API payload:', payload);
    
    let response;
    
    // Determine if we're updating or creating based on activeId
    if (activeId && activeId.length > 1) {
      Logger.log(`[handleSave] UPDATING CONTENT - ID: ${activeId}`);
      
      // For updates, we don't need the type field
      const updatePayload = {
        details: payload.details
      };
      
      response = await updateContent(updatePayload, activeId);
    } else {
      Logger.log('[handleSave] CREATING NEW CONTENT');
      response = await createNewContent(payload);
    }
    
    Logger.log(`[handleSave] API response: ${response?.status}`, response?.data);
    
    if (response?.status === 200) {
      toast.success('Email template saved successfully!');
      setHasUnsavedChanges(false);
      setOpenEmailBox(false);
      
      // Call the refresh function if it exists
      if (onSuccessfulSubmit) {
        await onSuccessfulSubmit();
      }
    } else {
      console.error('[handleSave] API returned non-200 status:', response);
      toast.error('Error saving email template');
    }
  } catch (error) {
    console.error('[handleSave] Error saving template:', error);
    if (error instanceof Error) {
      toast.error(`Failed to save email template: ${error.message}`);
    } else {
      toast.error('Failed to save email template. Please try again.');
    }
  } finally {
    setIsSubmitting(false);
  }
}, [emailData, validateForm, activeId, setOpenEmailBox, onSuccessfulSubmit, isHtmlTemplate, rawHtmlContent]);



// Wrapper function to adapt email data to the format expected by handleFormSubmit
const submitEmailTemplate = async (emailData: EmailData): Promise<any> => {
  Logger.log('[submitEmailTemplate] Original email data:', emailData);

  // Since handleFormSubmit expects an EmailData object, we should pass the emailData directly
  // or create a new object that matches the EmailData interface
  const formattedData: EmailData = {
    title: emailData.title,
    tags: emailData.tags,
    subject: emailData.subject,
    message: emailData.message,
    type: emailData.type || 'email_template',
    files: emailData.files,
    personalizedFields: emailData.personalizedFields,
   
  };

  Logger.log('[submitEmailTemplate] Formatted data:', formattedData);

  // Call the actual form submission function with data that matches EmailData interface
  return await handleFormSubmit(formattedData);
};


  // Handle close with unsaved changes
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      setOpenEmailBox(false);
    }
  }, [hasUnsavedChanges]);

  // ReactQuill modules configuration
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ],
  }), []);


const importHtmlTemplate = useCallback(() => {
  Logger.log('[importHtmlTemplate] Starting HTML template import');
  
  if (!htmlImportContent) {
    Logger.log('[importHtmlTemplate] No content to import');
    return;
  }
  
  try {
    // Basic validation
    if (!htmlImportContent.includes('<html') || !htmlImportContent.includes('</html>')) {
      Logger.log('[importHtmlTemplate] HTML validation failed - missing <html> tags');
      setHtmlImportError('HTML appears incomplete. Make sure it contains <html> tags.');
      return;
    }
    
    Logger.log('[importHtmlTemplate] HTML validation passed, storing raw content');
    
    // Store raw HTML
    setRawHtmlContent(htmlImportContent);
    
    // Set a placeholder in the editor
    handleInputChange("message", "<p>[HTML Template Imported - Preview in email preview]</p>");
    
    // Set flag
    setIsHtmlTemplate(true);
    setHasUnsavedChanges(true);
    setShowHtmlImportModal(false);
    
    Logger.log('[importHtmlTemplate] HTML template imported successfully');
    toast.success('HTML template imported successfully');
  } catch (error) {

    console.error('Error importing HTML template:', error);
    toast.error('Failed to import HTML template');
  }
}, [htmlImportContent, handleInputChange]);

  // Header actions for PopupComponent
const headerActions = useMemo(() => (
  <ButtonGroup>
    <Button onClick={handlePreviewToggle}>
      <Eye size={16} /> Preview
    </Button>
    
    {activeId && ( // Only show delete button for existing templates
      <Button 
        onClick={() => setShowDeleteModal(true)}
        style={{
          backgroundColor: '#fee2e2',
          color: '#dc2626'
        }}
      >
        <Trash2 size={16} /> Delete
      </Button>
    )}
    
    <Button primary onClick={handleSave} disabled={isSubmitting}>
      {isSubmitting ? (
        "Saving..."
      ) : (
        <>
          <Save size={16} /> Save Template
        </>
      )}
    </Button>
  </ButtonGroup>
), [handlePreviewToggle, handleSave, isSubmitting, activeId]);

  return (
    <>
      <PopupComponent 
        isOpen={openEmailBox}
        onClose={handleClose}
        title={activeId ? "Edit Email Template" : "Add Email Template"}
        size="lg"
        loading={isSubmitting}
        headerActions={headerActions}
      >
        <StyledEmailEditor>
          <Content>
            <FormField>
              <label>Title</label>
              <Input
                type="text"
                placeholder="Enter title"
                value={emailData.title}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  Logger.log('Title changed:', e.target.value);
                  handleInputChange("title", e.target.value);
                }}
              />
            </FormField>

            <FormField>
              <label>Tags</label>
              <TagContainer>
                {messageTag.map((tag) => (
                  <StyledTag
                    key={tag.value}
                    active={emailData.tags[0] === tag.value}
                    onClick={() => handleTagToggle(tag.value)}
                  >
                    {tag.name.toLowerCase().replace(/_/g, " ")}
                  </StyledTag>
                ))}
              </TagContainer>
            </FormField>

            <FormField>
              <label>Subject</label>
              <Input
                type="text"
                placeholder="Enter subject"
                value={emailData.subject}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("subject", e.target.value)}
              />
            </FormField>

        
            <FormField>
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between', 
    marginBottom: '8px' 
  }}>
    <label>Message</label>
    <div style={{ display: 'flex', gap: '8px' }}>
      {/* Unsubscribe Link Button */}
      <Button 
        type="button"
        onClick={insertUnsubscribeLink}
        style={{ 
          display: 'flex',
          alignItems: 'center',
          padding: '6px 12px',
          fontSize: '12px',
          backgroundColor: '#f0f0f0',
          color: '#333',
          border: '1px solid #ddd',
          borderRadius: '4px',
          gap: '6px'
        }}
      >
        <Link size={14} /> Insert Unsubscribe Link
      </Button>
      
      {/* Import HTML Button - ADD THIS */}
<Button 
  type="button"
  onClick={() => setShowHtmlImportModal(true)}
  style={{ 
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#f0f0f0',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '4px',
    gap: '6px'
  }}
>
  <Code size={14} /> Import HTML
</Button>

      {/* Auto Personalize Emails Button */}
      <Button 
        type="button"
        onClick={() => setIsFieldDrawerOpen(true)}
        style={{ 
          display: 'flex',
          alignItems: 'center',
          padding: '6px 12px',
          fontSize: '12px',
          backgroundColor: '#f0f0f0',
          color: '#333',
          border: '1px solid #ddd',
          borderRadius: '4px',
          gap: '6px'
        }}
      >
        <PlusCircle size={14} /> Auto Personalize emails
      </Button>
    </div>
  </div>

{/* HTML Template Indicator */}
{isHtmlTemplate && (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    background: '#f0f9ff', 
    color: '#0369a1',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '13px',
    marginBottom: '8px'
  }}>
    <Code size={16} style={{ marginRight: '8px' }} />
    <span>HTML Template Mode - Use Preview to see the actual template</span>
    <button 
      onClick={() => {
        Logger.log('Switching from HTML template to rich text mode');
        setIsHtmlTemplate(false);
        setRawHtmlContent('');
        setHasUnsavedChanges(true);
      }} 
      style={{
        marginLeft: 'auto',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '50%',
        display: 'flex'
      }}
      title="Switch to rich text editing"
    >
      <X size={16} color="#0369a1" />
    </button>
  </div>
)}

  <StyledReactQuill
    ref={editorRef}
    value={emailData.message}
    onChange={(content) => {
      Logger.log('Quill Content Changed:', content);
      handleInputChange("message", content);
    }}
    modules={modules}
    placeholder="Compose your email..."
  />
</FormField>
          </Content>
        </StyledEmailEditor>
      </PopupComponent>

      {previewData.isOpen && (
        <EmailPreview
          isOpen={previewData.isOpen}
          onClose={() => setPreviewData(prev => ({ ...prev, isOpen: false }))}
          subject={previewData.subject}
          content={previewData.content}
          attachments={previewData.attachments}
        />
        
      )}

     <GenericConfirmationModal
    showModal={showConfirmModal}
    toggleModal={() => setShowConfirmModal(false)}
    onConfirmation={handleConfirmClose}
    title="Unsaved Changes"
    message="You have unsaved changes. Are you sure you want to close?"
    confirmText="Yes, Close"
    cancelText="Cancel"
    isWarning={true}
  />
 

 <PopupComponent
  isOpen={isFieldDrawerOpen}
  onClose={() => setIsFieldDrawerOpen(false)}
  title="Insert Personalization Field"
  size="sm"
>
  <div 
    style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      maxHeight: '400px', 
      overflowY: 'auto',
      overflowX: 'hidden', // Prevent horizontal scrolling
      width: '100%' 
    }}
  >
    {(availableFields.length > 0 ? availableFields : reduxFields).map((field: any) => (
      <Button
        key={field.value}
        onClick={() => handleFieldInsertion({
          value: field.value,
          label: field.label || field.name
        })}
        style={{ 
          display: 'block', 
          width: '100%', 
          textAlign: 'left',
          marginBottom: '8px',
          padding: '10px',
          background: '#f0f0f0',
          border: 'none',
          borderRadius: '4px'
        }}
      >
        {field.label || field.name}
      </Button>
    ))}
  </div>
</PopupComponent>
<GenericConfirmationModal
      showModal={showDeleteModal}
      toggleModal={() => setShowDeleteModal(false)}
      onConfirmation={handleDelete}
      title="Delete Email Template"
      message="Are you sure you want to delete this email template? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      isWarning={true}
    />
    {/* HTML Import Modal - ADD THIS */}
<PopupComponent
  isOpen={showHtmlImportModal}
  onClose={() => {
    setShowHtmlImportModal(false);
    setHtmlImportContent('');
    setHtmlImportError('');
  }}
  title="Import HTML Email Template"
  size="md"
>
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 12px' }}>
    <div>
      <p style={{ marginBottom: '12px', fontSize: '14px', color: '#4b5563' }}>
        Paste your complete HTML email template code below. This will replace the current editor content.
      </p>
      <textarea
        value={htmlImportContent}
        onChange={(e) => setHtmlImportContent(e.target.value)}
        style={{
          width: '100%',
          minHeight: '300px',
          padding: '12px',
          fontFamily: 'monospace',
          fontSize: '14px',
          border: '1px solid #ced4da',
          borderRadius: '4px',
          resize: 'vertical'
        }}
        placeholder="<!DOCTYPE html>..."
      />
    </div>
    
    {htmlImportError && (
      <div style={{ color: '#dc2626', fontSize: '14px', padding: '8px', background: '#fee2e2', borderRadius: '4px' }}>
        {htmlImportError}
      </div>
    )}
    
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
      <Button 
        onClick={() => {
          setShowHtmlImportModal(false);
          setHtmlImportContent('');
          setHtmlImportError('');
        }}
      >
        Cancel
      </Button>
      <Button primary onClick={importHtmlTemplate}>
        Import Template
      </Button>
    </div>
  </div>
</PopupComponent>

</>

  );
};

export default EmailDashboard;